import cn from 'classnames'

import { Translation } from 'cng-common/features/translations/components/translation'
import { ITableColumn } from 'partner360/components/financialTable/'
import { TRANSLATION_KEYS } from 'partner360/components/QBR/translations'
import { IQBRPlanAchievementDependOnSettings } from 'partner360/models/qbr/IQBRPlanAchievementDependOnSettings'

import { QbrQuarters } from '../../../../../../models/qbrQuarters'
import styles from '../PlanAchievement.module.scss'

import { getUnitsViewCell } from './getUnitsViewCell'

interface IGetColumnsUnitsShortedViewParameters {
  selectedPeriodFQuarters: number[]
  showUpToDateColumn: boolean
}

export const getColumnsUnitsView = ({
  selectedPeriodFQuarters,
  showUpToDateColumn,
}: IGetColumnsUnitsShortedViewParameters): ITableColumn<IQBRPlanAchievementDependOnSettings>[] => {
  const previousYearColumns: ITableColumn<IQBRPlanAchievementDependOnSettings>[] = [
    {
      key: 'name',
      title: 'Name',
      renderFn: (item) => {
        return (
          <div className={styles.tableCol}>
            <p className={cn(styles.titleText, styles.name)}>{item.Name}</p>
            <div className={cn(styles.financialValue, styles.fullView)}>
              <p className={styles.paragraph}>
                <Translation span text={TRANSLATION_KEYS.financialTable.actual} />
              </p>
              <p className={styles.paragraph}>
                <Translation span text={TRANSLATION_KEYS.financialTable.plan} />
              </p>
              <p className={styles.subParagraph}>
                <Translation span text={TRANSLATION_KEYS.financialTable.actToPlan} />
              </p>
              <p className={styles.subParagraph}>
                <Translation span text={TRANSLATION_KEYS.financialTable.yoy} />
              </p>
            </div>
          </div>
        )
      },
    },
    {
      key: 'Q1',
      title: TRANSLATION_KEYS.financialTable.columns.Q1,
      isTranslatable: true,
      renderFn: (item) => {
        return getUnitsViewCell({
          actualsUnits: item.PrevYearQ1ActualsUnits,
          plansUnits: item.PrevYearQ1PlansUnits,
          actualsToPlans: item.PrevYearQ1ActualsToPlansUnits,
          yoyActuals: item.PrevYearQ1YoYActualsUnits,
        })
      },
    },
    {
      key: 'Q2',
      title: TRANSLATION_KEYS.financialTable.columns.Q2,
      isTranslatable: true,
      renderFn: (item) => {
        return getUnitsViewCell({
          actualsUnits: item.PrevYearQ2ActualsUnits,
          plansUnits: item.PrevYearQ2PlansUnits,
          actualsToPlans: item.PrevYearQ2ActualsToPlansUnits,
          yoyActuals: item.PrevYearQ2YoYActualsUnits,
        })
      },
    },
    {
      key: 'Q3',
      title: TRANSLATION_KEYS.financialTable.columns.Q3,
      isTranslatable: true,
      renderFn: (item) => {
        return getUnitsViewCell({
          actualsUnits: item.PrevYearQ3ActualsUnits,
          plansUnits: item.PrevYearQ3PlansUnits,
          actualsToPlans: item.PrevYearQ3ActualsToPlansUnits,
          yoyActuals: item.PrevYearQ3YoYActualsUnits,
        })
      },
    },
    {
      key: 'Q4',
      title: TRANSLATION_KEYS.financialTable.columns.Q4,
      isTranslatable: true,
      renderFn: (item) => {
        return getUnitsViewCell({
          actualsUnits: item.PrevYearQ4ActualsUnits,
          plansUnits: item.PrevYearQ4PlansUnits,
          actualsToPlans: item.PrevYearQ4ActualsToPlansUnits,
          yoyActuals: item.PrevYearQ4YoYActualsUnits,
        })
      },
    },
  ]

  if (showUpToDateColumn) {
    previousYearColumns.push({
      key: 'Total UTD',
      title: TRANSLATION_KEYS.financialTable.columns.totalUpToDate,
      isTranslatable: true,
      renderFn: (item) => {
        return getUnitsViewCell({
          actualsUnits: item.PrevYearFYActualsUnits,
          plansUnits: item.PrevYearFYPlansUnits,
          actualsToPlans: item.PrevYearFYActualsToPlansUnits,
          yoyActuals: item.PrevYearFYYoYActualsUnits,
        })
      },
    })
  }

  previousYearColumns.push({
    key: 'Total',
    title: TRANSLATION_KEYS.financialTable.columns.total,
    isTranslatable: true,
    renderFn: (item) => {
      return getUnitsViewCell({
        actualsUnits: item.PrevYearTFYActualsUnits,
        plansUnits: item.PrevYearTFYPlansUnits,
        actualsToPlans: item.PrevYearTFYActualsToPlansUnits,
        yoyActuals: item.PrevYearTFYYoYActualsUnits,
        isTotal: true,
      })
    },
  })

  const currentYearColumns: ITableColumn<IQBRPlanAchievementDependOnSettings>[] = []

  if (selectedPeriodFQuarters.includes(QbrQuarters.Q1)) {
    currentYearColumns.push({
      key: 'Q1',
      title: TRANSLATION_KEYS.financialTable.columns.Q1,
      isTranslatable: true,
      renderFn: (item) => {
        return getUnitsViewCell({
          actualsUnits: item.CurrentYearQ1ActualsUnits,
          plansUnits: item.CurrentYearQ1PlansUnits,
          actualsToPlans: item.CurrentYearQ1ActualsToPlansUnits,
          yoyActuals: item.CurrentYearQ1YoYActualsUnits,
        })
      },
    })
  }

  if (selectedPeriodFQuarters.includes(QbrQuarters.Q2)) {
    currentYearColumns.push({
      key: 'Q2',
      title: TRANSLATION_KEYS.financialTable.columns.Q2,
      isTranslatable: true,
      renderFn: (item) => {
        return getUnitsViewCell({
          actualsUnits: item.CurrentYearQ2ActualsUnits,
          plansUnits: item.CurrentYearQ2PlansUnits,
          actualsToPlans: item.CurrentYearQ2ActualsToPlansUnits,
          yoyActuals: item.CurrentYearQ2YoYActualsUnits,
        })
      },
    })
  }

  if (selectedPeriodFQuarters.includes(QbrQuarters.Q3)) {
    currentYearColumns.push({
      key: 'Q3',
      title: TRANSLATION_KEYS.financialTable.columns.Q3,
      isTranslatable: true,
      renderFn: (item) => {
        return getUnitsViewCell({
          actualsUnits: item.CurrentYearQ3ActualsUnits,
          plansUnits: item.CurrentYearQ3PlansUnits,
          actualsToPlans: item.CurrentYearQ3ActualsToPlansUnits,
          yoyActuals: item.CurrentYearQ3YoYActualsUnits,
        })
      },
    })
  }

  if (selectedPeriodFQuarters.includes(QbrQuarters.Q4)) {
    currentYearColumns.push({
      key: 'Q4',
      title: TRANSLATION_KEYS.financialTable.columns.Q4,
      isTranslatable: true,
      renderFn: (item) => {
        return getUnitsViewCell({
          actualsUnits: item.CurrentYearQ4ActualsUnits,
          plansUnits: item.CurrentYearQ4PlansUnits,
          actualsToPlans: item.CurrentYearQ4ActualsToPlansUnits,
          yoyActuals: item.CurrentYearQ4YoYActualsUnits,
        })
      },
    })
  }

  currentYearColumns.push({
    key: 'Total',
    title: showUpToDateColumn
      ? TRANSLATION_KEYS.financialTable.columns.totalUpToDate
      : TRANSLATION_KEYS.financialTable.columns.total,
    isTranslatable: true,
    renderFn: (item) => {
      return getUnitsViewCell({
        actualsUnits: item.CurrentYearFYActualsUnits,
        plansUnits: item.CurrentYearFYPlansUnits,
        actualsToPlans: item.CurrentYearFYActualsToPlansUnits,
        yoyActuals: item.CurrentYearFYYoYActualsUnits,
        isTotal: true,
      })
    },
  })

  return [...previousYearColumns, ...currentYearColumns]
}
